import { TreatmentAppointmentOutcomeBadge } from "../TreatmentAppointmentOutcomeBadge";
import { Text } from "@/components/ui";
import { MatterTreatmentAppointmentContextMenu } from "@/components/features/matter/context-menus/MatterTreatmentAppointmentContextMenu";
import { AppointmentStatusEditorPopover } from "@/components/features/matter/medical-treatments/AppointmentStatusEditorPopover";
import { Badge } from "@/components/ui/shadcn/Badge/Badge";
import { Button } from "@/components/ui/shadcn/Button/Button";

export type ProviderChipVariant =
  | "blue"
  | "green"
  | "purple"
  | "amber"
  | "rose"
  | "cyan"
  | "red";

export const providerChipVariants: ProviderChipVariant[] = [
  "blue",
  "green",
  "purple",
  "amber",
  "rose",
  "cyan",
  "red",
];
import { useUser } from "@/components/UserProvider";
import type { TreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useDeleteMedicalTreatmentAppointment } from "@/lib/data/react-query/matters/medical-treatment-appointment";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Connection, MedicalTreatment } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { formatDate, formatTime } from "@/lib/utils/format";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import {
  type Dispatch,
  type ReactNode,
  type ButtonHTMLAttributes,
  type SetStateAction,
  forwardRef,
} from "react";

interface MatterTreatmentAppointmentsCardProps {
  matterId: string;
  treatmentId?: string;
  appointments: (TreatmentViewModel["appointments"][number] & {
    provider?: Connection;
    type?: MedicalTreatment["type"];
  })[];
  expanded: "appointments" | "none";
  showProvider?: boolean;
  setExpanded?: Dispatch<SetStateAction<"appointments" | "none">>;
  contentClassName?: string;
  extraHeaderContent?: ReactNode;
  leftButtons?: ReactNode;
  rightButtons?: ReactNode;
}

function AppointmentStatusDot({
  notApplicable,
  label,
  tone,
  interactive = false,
}: {
  notApplicable: boolean;
  label: string;
  tone:
    | "received"
    | "ordered"
    | "notOrdered"
    | "rejected"
    | "partial"
    | "notApplicable";
  interactive?: boolean;
}) {
  if (notApplicable) {
    return (
      <Text
        variant="caption"
        color="muted"
        as="span"
        className={cn(interactive && "inline-flex items-center gap-1")}
      >
        N/A
        {interactive && <ChevronDown className="h-3 w-3" />}
      </Text>
    );
  }
  return (
    <span
      className={cn(
        "flex items-center gap-1 text-xs font-medium",
        tone === "received" && "text-green-600",
        tone === "ordered" && "text-amber-500",
        tone === "notOrdered" && "text-rose-600",
        tone === "rejected" && "text-rose-600",
        tone === "partial" && "text-amber-500",
        tone === "notApplicable" && "text-muted-foreground",
      )}
    >
      <span
        className={cn(
          "inline-block h-1.5 w-1.5 rounded-full",
          tone === "received" && "bg-green-600",
          tone === "ordered" && "bg-amber-500",
          tone === "notOrdered" && "bg-rose-600",
          tone === "rejected" && "bg-rose-600",
          tone === "partial" && "bg-amber-500",
          tone === "notApplicable" && "bg-muted-foreground",
        )}
      />
      <span
        className={cn(
          interactive &&
            "decoration-current underline-offset-2 group-focus-visible:underline",
        )}
      >
        {label}
      </span>
      {interactive && <ChevronDown className="h-3 w-3" />}
    </span>
  );
}

type AppointmentStatusSummary = {
  label: "Received" | "Requested" | "Not requested" | "Rejected" | "Partial";
  tone:
    | "received"
    | "ordered"
    | "notOrdered"
    | "rejected"
    | "partial"
    | "notApplicable";
};

const PreviewTriggerButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(function PreviewTriggerButton(
  { children, disabled, onClick, className, ...props },
  ref,
) {
  if (disabled) {
    return <div className="rounded-sm text-left">{children}</div>;
  }

  return (
    <button
      ref={ref}
      type="button"
      aria-label={props["aria-label"] ?? "Edit status"}
      title={props.title ?? "Edit status"}
      className={cn(
        "group focus-visible:ring-ring cursor-pointer rounded-full border border-transparent px-2 py-1 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none",
        "hover:border-border hover:bg-muted/60",
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      {...props}
    >
      {children}
    </button>
  );
});

const getAppointmentStatusSummary = <
  T extends {
    recordStatus?: string | null;
    status?: string | null;
    hasReference?: boolean;
  },
>(
  items: T[],
): AppointmentStatusSummary => {
  const uniqueStatuses = new Set(
    items
      .map((item) => item.recordStatus ?? item.status)
      .filter((status): status is string => Boolean(status)),
  );

  if (uniqueStatuses.size === 0) {
    return { label: "Not requested", tone: "notOrdered" };
  }

  if (uniqueStatuses.size > 1) {
    return { label: "Partial", tone: "partial" };
  }

  const [status] = Array.from(uniqueStatuses);

  if (status === "Received") {
    return { label: "Received", tone: "received" };
  }

  if (status === "Rejected") {
    return { label: "Rejected", tone: "rejected" };
  }

  if (status === "Requested") {
    return { label: "Requested", tone: "ordered" };
  }

  return { label: "Not requested", tone: "notOrdered" };
};

export function MatterTreatmentAppointmentsCard({
  matterId,
  treatmentId,
  appointments,
  expanded,
  showProvider = false,
  setExpanded,
  extraHeaderContent,
  leftButtons,
  rightButtons,
}: MatterTreatmentAppointmentsCardProps) {
  const { hasActionPermission } = useUser();
  const { openTreatmentAppointmentModal } = useGlobalModal();
  const deleteAppointment = useDeleteMedicalTreatmentAppointment();
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const isExpanded = expanded === "appointments";
  const canCreateAppointments = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointment,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateAppointments = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointment,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteAppointments = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentAppointment,
    ACTION_PERMISSION_VALUES.delete,
  );

  const sortedAppointments = [...appointments].sort((a, b) => {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return dayjs(b.date).diff(dayjs(a.date));
  });

  const datesWithValues = appointments.filter((a) => a.date);
  const sortedByDate = [...datesWithValues].sort((a, b) =>
    dayjs(a.date).diff(dayjs(b.date)),
  );
  const firstDate = sortedByDate[0]?.date;
  const lastDate = sortedByDate[sortedByDate.length - 1]?.date;
  const appointmentCount = appointments.length;

  return (
    <>
      <div className="border-border bg-card overflow-hidden rounded-lg border">
        <button
          type="button"
          onClick={() => {
            if (setExpanded) {
              setExpanded((prev) =>
                prev === "appointments" ? "none" : "appointments",
              );
            }
          }}
          className="hover:bg-muted/50 flex w-full items-center gap-2 px-4 py-3 transition-colors"
        >
          <ChevronRight
            className={cn(
              "text-muted-foreground h-4 w-4 shrink-0 transition-transform",
              isExpanded && "rotate-90",
            )}
          />
          <Text variant="body" as="span" className="font-semibold">
            Treatment dates
          </Text>
          <Badge variant="secondary" className="text-xs">
            {appointmentCount}{" "}
            {appointmentCount === 1 ? "appointment" : "appointments"}
          </Badge>
          {leftButtons}
          <div className="text-muted-foreground ml-auto flex items-center gap-4 text-xs">
            {firstDate && (
              <Text variant="caption" color="muted" as="span">
                First: {dayjs(firstDate).format("MMM D, YYYY")}
              </Text>
            )}
            {lastDate && lastDate !== firstDate && (
              <Text variant="caption" color="muted" as="span">
                Last: {dayjs(lastDate).format("MMM D, YYYY")}
              </Text>
            )}
            {rightButtons}
            {extraHeaderContent}
          </div>
        </button>

        {isExpanded && (
          <>
            {sortedAppointments.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <Text variant="subHeading">No treatment dates added yet</Text>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-border bg-muted/30 border-b">
                      <th className="px-4 py-2 text-left">
                        <Text variant="caption" color="muted">
                          Date
                        </Text>
                      </th>
                      {showProvider && (
                        <th className="px-4 py-2 text-left">
                          <Text variant="caption" color="muted">
                            Provider
                          </Text>
                        </th>
                      )}
                      <th className="px-4 py-2 text-left">
                        <Text variant="caption" color="muted">
                          Type
                        </Text>
                      </th>
                      <th className="px-4 py-2 text-left">
                        <Text variant="caption" color="muted">
                          Outcome
                        </Text>
                      </th>
                      <th className="px-4 py-2 text-left">
                        <Text variant="caption" color="muted">
                          Bills
                        </Text>
                      </th>
                      <th className="px-4 py-2 text-left">
                        <Text variant="caption" color="muted">
                          Records
                        </Text>
                      </th>
                      <th className="px-4 py-2 text-right">
                        <Text variant="caption" color="muted">
                          Bill total
                        </Text>
                      </th>
                      <th className="px-4 py-2 text-right">
                        <Text variant="caption" color="muted">
                          Outstanding
                        </Text>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-border divide-y">
                    {sortedAppointments.map((appointment) => {
                      const appointmentId = appointment.id;
                      const types = (
                        appointment.type ??
                        appointment.type ??
                        []
                      ).join(", ");
                      const billsNotApplicable =
                        appointment.billNotApplicable ||
                        appointment.billsNotApplicable;
                      const recordsNotApplicable =
                        appointment.recordNotApplicable ||
                        appointment.recordsNotApplicable;
                      const billStatusSummary = getAppointmentStatusSummary(
                        appointment.bills,
                      );
                      const recordStatusSummary = getAppointmentStatusSummary(
                        appointment.records,
                      );

                      const { totalBill, totalOutstanding } =
                        calculateMedicalTreatmentBillTotals(appointment.bills);
                      const appointmentTimeLabel = formatTime(appointment.time);

                      const effectiveTreatmentId =
                        treatmentId ?? appointment.medicalTreatmentId;
                      const dateLabel = [
                        formatDate(appointment.date),
                        appointmentTimeLabel && `at ${appointmentTimeLabel}`,
                        appointment.dateEnd &&
                          `– ${formatDate(appointment.dateEnd)}`,
                      ]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <MatterTreatmentAppointmentContextMenu
                          key={appointmentId}
                          canDelete={canDeleteAppointments}
                          dateLabel={dateLabel}
                          id={appointmentId}
                          isDeleting={deleteAppointment.isPending}
                          matter={matter}
                          onDelete={async () => {
                            await deleteAppointment.mutateAsync({
                              matterId,
                              id: appointmentId,
                            });
                          }}
                          onEdit={() => {
                            if (canUpdateAppointments && effectiveTreatmentId) {
                              openTreatmentAppointmentModal({
                                matterId,
                                appointmentId,
                                treatmentId: effectiveTreatmentId,
                                editing: true,
                                initialTab: "details",
                              });
                            }
                          }}
                          typeLabel={types}
                          trigger={
                            <tr
                              className={cn(
                                canUpdateAppointments &&
                                  effectiveTreatmentId &&
                                  "hover:bg-accent/30 cursor-pointer transition-colors",
                              )}
                              onClick={() => {
                                if (
                                  canUpdateAppointments &&
                                  effectiveTreatmentId
                                ) {
                                  openTreatmentAppointmentModal({
                                    matterId,
                                    appointmentId,
                                    treatmentId: effectiveTreatmentId,
                                    editing: true,
                                    initialTab: "details",
                                  });
                                }
                              }}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <Text
                                  variant="body"
                                  className="font-semibold whitespace-nowrap"
                                >
                                  {formatDate(appointment.date)}
                                  {appointmentTimeLabel &&
                                    ` at ${appointmentTimeLabel}`}
                                  {appointment.dateEnd &&
                                    ` – ${formatDate(appointment.dateEnd)}`}
                                </Text>
                              </td>
                              {showProvider && (
                                <td className="px-4 py-3">
                                  <Text
                                    variant="subHeading"
                                    className="break-words"
                                  >
                                    {getConnectionContactName(
                                      appointment.provider?.contact,
                                    ) || "Provider not assigned"}
                                  </Text>
                                </td>
                              )}
                              <td className="px-4 py-3">
                                <Text variant="subHeading">{types || "—"}</Text>
                              </td>
                              <td className="min-w-40 px-4 py-3">
                                <TreatmentAppointmentOutcomeBadge
                                  appointment={appointment}
                                />
                              </td>
                              <td className="px-4 py-3">
                                <AppointmentStatusEditorPopover
                                  appointmentId={appointmentId}
                                  matter={matter}
                                  type="bill"
                                  items={appointment.bills}
                                  treatmentId={effectiveTreatmentId}
                                >
                                  <PreviewTriggerButton
                                    disabled={
                                      !effectiveTreatmentId ||
                                      Boolean(billsNotApplicable)
                                    }
                                  >
                                    <AppointmentStatusDot
                                      notApplicable={Boolean(
                                        billsNotApplicable,
                                      )}
                                      label={billStatusSummary.label}
                                      tone={billStatusSummary.tone}
                                      interactive={
                                        Boolean(effectiveTreatmentId) &&
                                        !billsNotApplicable
                                      }
                                    />
                                  </PreviewTriggerButton>
                                </AppointmentStatusEditorPopover>
                              </td>
                              <td className="px-4 py-3">
                                <AppointmentStatusEditorPopover
                                  appointmentId={appointmentId}
                                  matter={matter}
                                  type="record"
                                  items={appointment.records}
                                  treatmentId={effectiveTreatmentId}
                                >
                                  <PreviewTriggerButton
                                    disabled={
                                      !effectiveTreatmentId ||
                                      Boolean(recordsNotApplicable)
                                    }
                                  >
                                    <AppointmentStatusDot
                                      notApplicable={Boolean(
                                        recordsNotApplicable,
                                      )}
                                      label={recordStatusSummary.label}
                                      tone={recordStatusSummary.tone}
                                      interactive={
                                        Boolean(effectiveTreatmentId) &&
                                        !recordsNotApplicable
                                      }
                                    />
                                  </PreviewTriggerButton>
                                </AppointmentStatusEditorPopover>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Text
                                  variant="subHeading"
                                  className="font-medium"
                                >
                                  {appointment.bills.length > 0
                                    ? new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                      }).format(totalBill)
                                    : "—"}
                                </Text>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Text
                                  variant="subHeading"
                                  className="font-medium"
                                >
                                  {appointment.bills.length > 0
                                    ? new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: "USD",
                                      }).format(totalOutstanding)
                                    : "—"}
                                </Text>
                              </td>
                            </tr>
                          }
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {canCreateAppointments && treatmentId && (
              <div className="border-border border-t px-4 py-3">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground hover:text-foreground h-7 gap-1.5 text-xs"
                  onClick={() =>
                    openTreatmentAppointmentModal({
                      matterId,
                      treatmentId,
                      editing: false,
                      initialTab: "details",
                    })
                  }
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add treatment date
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
